import { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { useLocale } from 'next-intl';

import useFeatureToggle from '@guestyci/feature-toggle-fe/useFeatureToggle';

import useIsMobile from 'hooks/useIsMobile';
import { GOOGLE_MAP_KEY } from 'constants/map';
import { cleanupGoogleMapsScripts } from '../utils';
import SuspenseLoader from './SuspenseLoader';
import { ODS_BE_NEW_MAP } from 'constants/featureToggleNames';

const MobileUI = lazy(() => import('./SearchPageGoogleMapMobile'));
const DesktopUI = lazy(() => import('./SearchPageGoogleMapDialog'));

const GoogleMapDialogProvider = () => {
  const [, isNewMapEnabled] = useFeatureToggle(ODS_BE_NEW_MAP);
  const isMobile = useIsMobile();
  const locale = useLocale();
  const [mapKey, setMapKey] = useState(0);
  const [isRemounting, setIsRemounting] = useState(false);

  const remountMap = useCallback(() => {
    setIsRemounting(true);
    cleanupGoogleMapsScripts();
    setMapKey(prev => prev + 1);
    setTimeout(() => setIsRemounting(false), 100);
  }, [locale]);

  useEffect(() => {
    const prevLanguage = localStorage.getItem('prevLanguage');
    if (prevLanguage && prevLanguage !== locale) {
      localStorage.setItem('prevLanguage', locale);
      remountMap();
    } else if (!prevLanguage) {
      localStorage.setItem('prevLanguage', locale);
    }
  }, [locale, remountMap]);

  if (isRemounting) return <SuspenseLoader />;
  if (!isNewMapEnabled) {
    return null;
  }

  return (
    <APIProvider key={mapKey} apiKey={GOOGLE_MAP_KEY} language={locale}>
      <Suspense fallback={<SuspenseLoader />}>
        {isMobile ? <MobileUI /> : <DesktopUI />}
      </Suspense>
    </APIProvider>
  );
};

export default GoogleMapDialogProvider;